import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { registerCompany } from "../../api/registerCompanyApi";

function Register() {
  const [formData, setFormData] = useState({
    companyName: "",
    dateOfRegistration: "",
    logo: null,
    mainContactName: "",
    email: "",
    contact: "",
    address: "",
    employeeLength: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showValidationRules, setShowValidationRules] = useState({});

  // Validation rules configuration
  const validationRules = {
    companyName: {
      rules: "Required, minimum 2 characters, maximum 100 characters",
      pattern: /^[a-zA-Z0-9\s&.,-]{2,100}$/
    },
    dateOfRegistration: {
      rules: "Required, cannot be future date",
      validate: (value) => {
        const selectedDate = new Date(value);
        const today = new Date();
        return selectedDate <= today;
      }
    },
    mainContactName: {
      rules: "Required, minimum 2 characters, only letters and spaces",
      pattern: /^[a-zA-Z\s]{2,50}$/
    },
    email: {
      rules: "Required, valid email format",
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    },
    contact: {
      rules: "Required, 10-15 digits, can include +, -, (, )",
      pattern: /^[\+]?[0-9\s\-\(\)]{10,15}$/
    },
    address: {
      rules: "Required, minimum 10 characters",
      minLength: 10
    },
    employeeLength: {
      rules: "Required, numbers only, minimum 1",
      pattern: /^[1-9]\d*$/
    },
    password: {
      rules: "Required, minimum 8 characters, at least one uppercase, one lowercase, one number and one special character",
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
    },
    logo: {
      rules: "Optional, must be image file (JPEG, PNG, JPG), max size 5MB",
      validate: (file) => {
        if (!file) return true;
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        const maxSize = 5 * 1024 * 1024; // 5MB
        return validTypes.includes(file.type) && file.size <= maxSize;
      }
    }
  };

  const validateField = (name, value) => {
    const rules = validationRules[name];
    if (!rules) return "";

    // Required field validation
    if (value === "" || value === null) {
      return "This field is required";
    }

    // Pattern validation
    if (rules.pattern && !rules.pattern.test(value)) {
      return `Invalid format. Rules: ${rules.rules}`;
    }

    // Custom validation function
    if (rules.validate && !rules.validate(value)) {
      return `Invalid value. Rules: ${rules.rules}`;
    }

    // Min length validation
    if (rules.minLength && value.length < rules.minLength) {
      return `Minimum ${rules.minLength} characters required`;
    }

    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
      if (files[0]) {
        const error = validateField(name, files[0]);
        setErrors(prev => ({ ...prev, [name]: error }));
      } else {
        setErrors(prev => ({ ...prev, [name]: "" }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // Real-time validation after user has touched the field
      if (touched[name]) {
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleFocus = (fieldName) => {
    setShowValidationRules(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleFieldBlur = (fieldName) => {
    setTimeout(() => {
      setShowValidationRules(prev => ({ ...prev, [fieldName]: false }));
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate entire form
    if (!validateForm()) {
      toast.error("Please fix the validation errors before submitting.");
      return;
    }

    try {
      const formDataToSend = new FormData();
      
      for (const key in formData) {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      }

      await registerCompany(formDataToSend);
      toast.success("Company registered successfully!");
      
      // Reset form
      setFormData({
        companyName: "",
        dateOfRegistration: "",
        logo: null,
        mainContactName: "",
        email: "",
        contact: "",
        address: "",
        employeeLength: "",
        password: "",
      });
      setErrors({});
      setTouched({});
      
    } catch (error) {
      console.error("Error registering company:", error);
      const errorMessage = error.response?.data?.message || "Failed to register company. Please try again.";
      toast.error(errorMessage);
    }
  };

  const getMaxDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <ToastContainer />
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Company Registration
          </h1>
          <p className="text-gray-600 text-lg">
            Create your company account in minutes
          </p>
        </div>

        {/* Validation Rules Panel */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
         
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• All fields marked with * are required</li>
            <li>• Click on any field to see specific validation rules</li>
            <li>• File uploads must be images under 5MB</li>
            <li>• Password must meet security requirements</li>
          </ul>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Company Information Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900 border-b pb-2">
                Company Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Company Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('companyName')}
                    onMouseLeave={() => handleFieldBlur('companyName')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.companyName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.companyName && (
                    <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                  )}
                  {showValidationRules.companyName && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                       
                      <p className="text-blue-700 text-xs">{validationRules.companyName.rules}</p>
                    </div>
                  )}
                </div>

                {/* Registration Date */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Registration Date *
                  </label>
                  <input
                    type="date"
                    name="dateOfRegistration"
                    value={formData.dateOfRegistration}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('dateOfRegistration')}
                    onMouseLeave={() => handleFieldBlur('dateOfRegistration')}
                    max={getMaxDate()}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.dateOfRegistration ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.dateOfRegistration && (
                    <p className="text-red-500 text-sm mt-1">{errors.dateOfRegistration}</p>
                  )}
                  {showValidationRules.dateOfRegistration && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                       
                      <p className="text-blue-700 text-xs">{validationRules.dateOfRegistration.rules}</p>
                    </div>
                  )}
                </div>

                {/* Employee Count */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Employee Count *
                  </label>
                  <input
                    type="text"
                    name="employeeLength"
                    value={formData.employeeLength}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('employeeLength')}
                    onMouseLeave={() => handleFieldBlur('employeeLength')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.employeeLength ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.employeeLength && (
                    <p className="text-red-500 text-sm mt-1">{errors.employeeLength}</p>
                  )}
                  {showValidationRules.employeeLength && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                   
                      <p className="text-blue-700 text-xs">{validationRules.employeeLength.rules}</p>
                    </div>
                  )}
                </div>

                {/* Company Logo */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Company Logo
                  </label>
                  <div className="flex items-center space-x-3">
                    <label className="cursor-pointer flex-1">
                      <div className={`px-4 py-3 border-2 border-dashed rounded-lg hover:border-blue-400 transition text-center ${
                        errors.logo ? 'border-red-500' : 'border-gray-300'
                      }`}>
                        <span className="text-gray-600">
                          {formData.logo ? formData.logo.name : "Choose logo..."}
                        </span>
                      </div>
                      <input
                        type="file"
                        name="logo"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="hidden"
                        accept="image/*"
                      />
                    </label>
                  </div>
                  {errors.logo && (
                    <p className="text-red-500 text-sm mt-1">{errors.logo}</p>
                  )}
                  {formData.logo && !errors.logo && (
                    <p className="text-green-500 text-sm mt-1">✓ File selected successfully</p>
                  )}
                  {showValidationRules.logo && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                      
                      <p className="text-blue-700 text-xs">{validationRules.logo.rules}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900 border-b pb-2">
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Contact Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="mainContactName"
                    value={formData.mainContactName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('mainContactName')}
                    onMouseLeave={() => handleFieldBlur('mainContactName')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.mainContactName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.mainContactName && (
                    <p className="text-red-500 text-sm mt-1">{errors.mainContactName}</p>
                  )}
                  {showValidationRules.mainContactName && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                  
                      <p className="text-blue-700 text-xs">{validationRules.mainContactName.rules}</p>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('email')}
                    onMouseLeave={() => handleFieldBlur('email')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                  {showValidationRules.email && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                      <p className="text-blue-700 text-xs">{validationRules.email.rules}</p>
                    </div>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('password')}
                    onMouseLeave={() => handleFieldBlur('password')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                  )}
                  {showValidationRules.password && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                      <p className="text-blue-700 text-xs">{validationRules.password.rules}</p>
                    </div>
                  )}
                </div>

                {/* Contact Number */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Contact Number *
                  </label>
                  <input
                    type="text"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('contact')}
                    onMouseLeave={() => handleFieldBlur('contact')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.contact ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.contact && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact}</p>
                  )}
                  {showValidationRules.contact && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                      <p className="text-blue-700 text-xs">{validationRules.contact.rules}</p>
                    </div>
                  )}
                </div>

                {/* Address */}
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus('address')}
                    onMouseLeave={() => handleFieldBlur('address')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.address && (
                    <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                  )}
                  {showValidationRules.address && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-1">
                      <p className="text-blue-700 text-xs">{validationRules.address.rules}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition transform hover:-translate-y-0.5 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={Object.keys(errors).some(key => errors[key])}
              >
                Register Company
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;